import { json } from "react-router-dom";
import { queuedFetch } from "./requestQueue";
import {
    accumulateMetadata,
    transformDataLineChart,
    transformDataStackedBar,
    transformDataBarChart,
    transformDataMultipleAxis,
    transformDataPyramid,
    transformDataPie,
    transformDataStatusChart
} from "../utils/charts";
// Single place where an indicator query becomes a request URL, so the review-only debug
// panel always shows the call that was actually made. `query` arrives already encoded.
export const buildBigQueryUrl = (query) =>
    `https://data.fao.org/api/v2/bigquery?query=${query}&output_format=json&download=false`;
//  `https://data.review.fao.org/bigquery?query=${query}&output_format=json&download=false`;


export const fetchData = async (url, info, countryCode, comparingCountries = [], signal) => {
    try {
        const response = await queuedFetch(url, { signal });
        if (!response.ok) {
            // The BigQuery proxy returns a JSON body like {"detail": "..."} describing what
            // actually went wrong (bad query, rate limit, etc.) - surface that instead of a
            // generic message, so errors shown in the UI are actually actionable. Gateway-level
            // failures (e.g. 502/504 from a proxy/load balancer) often return plain text or HTML
            // instead of JSON, so fall back to reading the body as text in that case.
            let detail = `HTTP ${response.status} ${response.statusText}`;
            const bodyText = await response.text().catch(() => "");
            if (bodyText) {
                try {
                    const errorBody = JSON.parse(bodyText);
                    if (errorBody?.detail) detail = errorBody.detail;
                } catch {
                    // Not JSON - use the raw text (trimmed, and capped so an HTML error page
                    // doesn't dump its whole markup into the UI).
                    const trimmed = bodyText.replace(/\s+/g, " ").trim();
                    if (trimmed) detail = `${detail} — ${trimmed.slice(0, 200)}`;
                }
            }
            throw new Error(detail);
        }
        const data = await response.json();

        // Normalize 'area' field if it is missing but 'label' is present (e.g. for vw_oea and vw_oer views)
        const normalizedData = Array.isArray(data)
            ? data.map(item => (item && !item.area && item.label) ? { ...item, area: item.label } : item)
            : data;

        // Thanks a lot, CSI.
        // countryCode = parseInt(countryCode);

        let transformFunction;
        switch (info.chart_type) {
            case "line":
                transformFunction = transformDataLineChart;
                break;
            case "barChart":
                transformFunction = transformDataBarChart;
                break;
            case "multipleAxis":
                transformFunction = transformDataMultipleAxis;
                break;
            case "pyramid":
                transformFunction = transformDataPyramid;
                break;
            case "pie":
            case "donut":
                transformFunction = transformDataPie;
                break;
            case "status":
                transformFunction = transformDataStatusChart;
                break;
            default:
                transformFunction = transformDataStackedBar;
        }

        // For line charts, only allow subregional comparison when:
        // - exclude_subregional_comparison is NOT true, and
        // - there is only a single combination across item/element/indicator
        const hasMultipleCombos = (
            Array.isArray(info.item_code) && info.item_code.length > 1
        ) || (
                Array.isArray(info.element_code) && info.element_code.length > 1
            ) || (
                Array.isArray(info.indicator_code) && info.indicator_code.length > 1
            );

        const canCompareSubregions = info.chart_type === "line"
            ? (!info.exclude_subregional_comparison && !hasMultipleCombos)
            : false;

        const transformed = transformFunction(
            normalizedData,
            info.element_code,
            info.item_code,
            countryCode,
            Array.isArray(info.sex_code) && info.sex_code.length > 0 ? info.sex_code : null,
            Array.isArray(info.food_group) && info.food_group.length > 0 ? info.food_group : null,
            info.isPoU,
            info.isFoodInsecurity,
            Array.isArray(info.indicator_code) && info.indicator_code.length > 0 ? info.indicator_code : null,
            info.use_item_labels ?? info.useSideBySideBars ?? null,
            canCompareSubregions ? comparingCountries : null,
            // Pass through the selected years (from UI) so transforms can align ticks to filter
            Array.isArray(info.selectedYears) ? info.selectedYears : null,
            info // Pass full info object so transform functions can access custom flags
        );

        return { transformed, originalData: normalizedData };

    } catch (error) {
        console.error('Failed to fetch data:', error);
        throw error;
    }
};



export const fetchSource = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const resData = await response.json();
        const transformed = accumulateMetadata(resData.data);

        return transformed;

    } catch (error) {
        console.error('Failed to fetch data:', error);
        return null;
    }
};

// The dim_documents rows behind the related links depend only on the domain - not on the
// country, nor on the selected years - yet every indicator card used to request its own
// copy, so a page issued dozens of identical queries and fired them all again on every
// country change. Cache per URL and share the in-flight promise: each domain is fetched
// once per session, which roughly halves the requests a page makes.
const relatedLinksCache = new Map();

export const fetchRelatedLinks = async (url, blacklist = []) => {
    try {
        if (!relatedLinksCache.has(url)) {
            const pending = (async () => {
                const response = await queuedFetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status} ${response.statusText}`);
                }
                return response.json();
            })();
            // A failure must not be cached, or one bad moment would poison the domain for
            // the rest of the session.
            relatedLinksCache.set(url, pending.catch((error) => {
                relatedLinksCache.delete(url);
                throw error;
            }));
        }

        const resData = await relatedLinksCache.get(url);
        return resData.filter(link => !blacklist.includes(link.file_name));

    } catch (error) {
        console.error('Failed to fetch data:', error);
        return null;
    }
};

export const fetchAreaGroups = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const resData = await response.json();

        const areaCountries = resData.map((item) => ({
            code: item.m49_code,
            label: item.country,
        }));

        return areaCountries;

    } catch (error) {
        console.error('Failed to fetch data:', error);
        // Consumers (e.g. the compare-countries Autocomplete) expect an array to map/filter
        // over - null crashes MUI's useAutocomplete, so fall back to an empty list on failure.
        return [];
    }
};

export const fetchRegions = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const resData = await response.json();

        const areaRegions = resData.map((item) => ({
            code: item.group_m49_code,
            label: item.country_group,
            subtype: item.subtype
        }));

        return areaRegions;

    } catch (error) {
        console.error('Failed to fetch data:', error);
        return [];
    }
};

export const getData = async (sql) => {
    try {
        const encodedQuery = encodeURIComponent(sql);
        const url = `https://data.fao.org/api/v2/bigquery?query=${encodedQuery}&output_format=json&download=false`;
        //const url = `https://data.review.fao.org/bigquery?query=${encodedQuery}&output_format=json&download=false`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const data = await response.json();

        return data;

    } catch (error) {
        console.error('Failed to fetch data:', error);
        return null;
    }
};


